<template>
  <div>
    <div
      class="rounded-md"
      :class="{ 'h-96': enlargeMap, 'h-64': !enlargeMap }"
    >
      <LMap
        ref="map"
        :zoom="zoom"
        :center="center"
        :options="mapOptions"
        style="height: 100%"
        @update:center="centerUpdate"
        @update:zoom="zoomUpdate"
        ><l-control-layers> </l-control-layers>
        <l-wms-tile-layer
          v-for="layer in layers"
          :key="layer.name"
          :base-url="layer.baseurl"
          :layers="layer.layers"
          :visible="layer.visible"
          :name="layer.name"
          layer-type="base"
        >
        </l-wms-tile-layer>
        <l-marker :lat-lng="withPopup" v-if="canEdit">
          <l-popup>
            <div>Locatie {{ acc.naam }}</div>
          </l-popup>
        </l-marker>
      </LMap>
    </div>
    <button
      @click="enlargeMap = !enlargeMap"
      class="bg-gray-100  text-blue-700 font-semibold py-1 px-2 border border-mi-blue-400 rounded hover:bg-mi-blue-400 hover:text-white hover:border-transparent mt-1"
    >
      <p v-if="!enlargeMap">Vergroot kaart</p>
      <p v-else>Verklein kaart</p>
    </button>
    <p class="text-red-600">{{ msg }}</p>
  </div>
</template>

<script>
import {
  latLng,
  Icon,
  Control,
  FeatureGroup,
  geoJson,
  LayerGroup
} from "leaflet";

import LDraw from "leaflet-draw";
import "leaflet-draw/dist/leaflet.draw.css";

import {
  LMap,
  LWMSTileLayer,
  LControlLayers,
  LMarker,
  LPopup
} from "vue2-leaflet";

// this is a quick fix for not loading icons in webpack
// https://vue2-leaflet.netlify.app/quickstart/#marker-icons-are-missing
delete Icon.Default.prototype._getIconUrl;
Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png")
});

const leafletDrawConfig = map => {
  // Initialise a drawlayer and add it to the map object
  const drawnItems = new FeatureGroup();
  map.$refs.map.mapObject.addLayer(drawnItems);

  // Initialise layer with other voorzieningen and add to map object
  const otherVoorzieningen = new FeatureGroup();
  map.$refs.map.mapObject.addLayer(otherVoorzieningen);

  // there are two controlbars: drawcontrol and editcontrol
  // here we define them
  const drawControl = new Control.Draw({
    edit: {
      featureGroup: drawnItems
    },
    draw: {
      polygon: true,
      polyline: true,
      circle: false,
      rectangle: false,
      marker: false,
      circlemarker: false
    }
  });

  if (map.canEdit) map.$refs.map.mapObject.addControl(drawControl);

  const editControl = new Control.Draw({
    edit: {
      featureGroup: drawnItems
    },
    draw: false
  });

  // magic helper function to add GeoJson objects to a sourcelayer (drawlayer)
  const addNonGroupLayers = (sourceLayer, targetGroup) => {
    if (sourceLayer instanceof LayerGroup) {
      sourceLayer.eachLayer(function(layer) {
        addNonGroupLayers(layer, targetGroup);
      });
    } else {
      targetGroup.addLayer(sourceLayer);
    }
  };

  // if there is a geometry: add it to the drawlayer ánd add the edit toolbar
  // we can only edit or add, not both
  if (map.vzGeometry) {
    const geoJsonGroup = geoJson(map.vzGeometry);

    addNonGroupLayers(geoJsonGroup, drawnItems);
    if (map.canEdit) {
      drawControl.remove();
      map.$refs.map.mapObject.addControl(editControl);
    }
  }

  if (map.geometryAndereVoorzieningen) {
    for (let vz of map.geometryAndereVoorzieningen) {
      let geoJsonGroup = geoJson(vz);
      geoJsonGroup.setStyle({
        fillColor: "#00FF00",
        color: "#00FF00",
        opacity: 0.5,
        fillOpacity: 0.3
      });
      addNonGroupLayers(geoJsonGroup, otherVoorzieningen);
    }
  }

  //event listener: pressing delete should submit delete query to backend
  map.$refs.map.mapObject.on("draw:deleted", function() {
    map.submitHandler(null);
    this.addControl(drawControl);
    editControl.remove();
  });

  //event listener: pressing save should submit edit query to backend
  map.$refs.map.mapObject.on("draw:edited", function({ layers }) {
    layers.eachLayer(function(layer) {
      map.submitHandler(layer.toGeoJSON());
    });
  });

  //event listener: on add should submit query to backend
  map.$refs.map.mapObject.on("draw:created", function({ layer }) {
    const json = layer.toGeoJSON();
    map.submitHandler(json);
    drawnItems.addLayer(layer);
    this.addControl(editControl);
    drawControl.remove();
  });
};

export default {
  name: "Map",
  components: {
    LMap,
    "l-wms-tile-layer": LWMSTileLayer,
    LControlLayers,
    LMarker,
    LPopup
  },
  props: ["acc"],
  data() {
    return {
      msg: null,
      zoom: 50,
      enlargeMap: false,
      center: latLng(this.acc.latlon[0], this.acc.latlon[1]),
      layers: [
        {
          name: "PDOK satelliet data (actueel)",
          visible: true,
          format: "image/png",
          layers: "Actueel_ortho25",
          transparent: true,
          baseurl: "https://service.pdok.nl/hwh/luchtfotorgb/wms/v1_0",
          attribution: ""
        },
        {
          name: "PDOK IR data (actueel)",
          visible: false,
          format: "image/png",
          layers: "Actueel_ortho25IR",
          transparent: true,
          baseurl: "https://service.pdok.nl/hwh/luchtfotocir/wms/v1_0",
          attribution: ""
        }
      ],
      withPopup: latLng(this.acc.latlon[0], this.acc.latlon[1]),
      currentZoom: 16,
      currentCenter: this.withPopup,
      showParagraph: false,
      mapOptions: {
        zoomSnap: 0.5
      },
      showMap: true,
      canEdit:
        this.hasPermission("update voorziening geometry") &&
        this.hasPermission("update voorziening")
    };
  },
  computed: {
    voorzieningen: function() {
      return this.$store.state.backend_data.voorzieningen.filter(
        vz => vz.accommodatie.id === this.acc.id
      );
    },
    vzGeometry: function() {
      return this.voorzieningen.find(vz => vz.id === this.$route.params.vzid)
        .geometrie;
    },
    geometryAndereVoorzieningen: function() {
      if (!this.canEdit) return null;
      return this.voorzieningen
        .filter(vz => vz.id !== this.$route.params.vzid && vz.geometrie)
        .map(vz => vz.geometrie);
    }
  },
  methods: {
    zoomUpdate(zoom) {
      this.currentZoom = zoom;
    },
    centerUpdate(center) {
      this.currentCenter = center;
    },
    parseResponse: function(ok, msg) {
      if (ok) {
        this.msg = "Gegevens succesvol opgeslagen";
      } else {
        this.msg = msg;
      }
    },
    hideMsg: function() {
      this.msg = null;
    },
    submitHandler: async function(data) {
      const updatedVoorziening = {
        geometrie: data
      };
      const [ok, msg] = await this.$store.dispatch(
        "updateVoorzieningGeometry",
        {
          id: this.$route.params.vzid,
          properties: updatedVoorziening
        }
      );
      this.parseResponse(ok, msg);
    }
  },
  watch: {
    msg() {
      if (this.msg) {
        setTimeout(this.hideMsg, 5000);
      }
    }
  },
  mounted() {
    this.$nextTick(() => leafletDrawConfig(this));
  }
};
</script>

<style>
.h-96 {
  height: 28rem;
}
</style>
